import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getComicBySlug, type ComicItem } from '@/mocks/comics';
import { getReadingImages } from '@/mocks/reading';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [comic, setComic] = useState<ComicItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    setSelectedChapter(null);

    const timer = setTimeout(() => {
      const found = getComicBySlug(slug || '');
      setComic(found || null);
      setLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [slug]);

  useEffect(() => {
    if (comic) {
      document.title = `${comic.title} - 韩漫在线观看`;
    }
  }, [comic]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="pt-24 min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-foreground-500">加载中...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="pt-24 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-4xl text-foreground-400" style={{ width: '40px', height: '40px' }}></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line" style={{ width: '16px', height: '16px' }}></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        {/* Comic Info Header */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
            {/* Breadcrumb */}
            <nav className="mb-6" aria-label="面包屑导航">
              <ol className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                </li>
                <li className="text-foreground-300">/</li>
                <li>
                  <Link to={`/category/${comic.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                    {comic.category === 'romance' ? '爱情' : comic.category === 'fantasy' ? '奇幻' : comic.category === 'action' ? '动作' : comic.category === 'mystery' ? '悬疑' : comic.category === 'comedy' ? '搞笑' : comic.category === 'drama' ? '剧情' : comic.category === 'horror' ? '恐怖' : '全部'}韩漫
                  </Link>
                </li>
                <li className="text-foreground-300">/</li>
                <li className="text-foreground-700 truncate">{comic.title}</li>
              </ol>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              {/* Cover */}
              <div className="w-full sm:w-48 md:w-56 flex-shrink-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 韩漫在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-user-line" style={{ width: '16px', height: '16px' }}></i>
                    {comic.author}
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-eye-line" style={{ width: '16px', height: '16px' }}></i>
                    {(comic.views / 10000).toFixed(1)}万次阅读
                  </span>
                  <span className="inline-flex items-center gap-1 text-sm text-foreground-600">
                    <i className="ri-star-fill text-accent-500" style={{ width: '16px', height: '16px' }}></i>
                    <strong>{comic.rating}</strong> 分
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span
                    className={`inline-flex px-3 py-1 rounded-full text-xs font-label ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-accent-100 text-accent-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="inline-flex px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-label">
                    共 {comic.chapters.length} 话
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {comic.tags.map((tag: string) => (
                    <span key={tag} className="inline-flex px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-xs">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed max-w-2xl">
                  {comic.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List / Reading Area */}
        <section className="py-10 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {!selectedChapter ? (
              <>
                <h2 className="font-heading text-xl text-foreground-900 mb-6">章节列表</h2>

                <div className="space-y-2">
                  {comic.chapters.map((chapter, index) => (
                    <button
                      key={chapter.id}
                      onClick={() => setSelectedChapter(index)}
                      className="w-full flex items-center justify-between gap-4 p-4 bg-background-100 rounded-xl border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 text-left cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-label">
                          {chapter.number}
                        </div>
                        <span className="font-heading text-sm text-foreground-800">
                          {chapter.title}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-foreground-400" style={{ width: '18px', height: '18px' }}></i>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                {/* Chapter Navigation */}
                <div className="flex items-center justify-between mb-8">
                  <button
                    onClick={() => setSelectedChapter(Math.max(0, selectedChapter - 1))}
                    disabled={selectedChapter === 0}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-label transition-colors cursor-pointer ${
                      selectedChapter === 0
                        ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                        : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500'
                    }`}
                  >
                    <i className="ri-arrow-left-s-line" style={{ width: '16px', height: '16px' }}></i>
                    上一话
                  </button>

                  <div className="text-center">
                    <h2 className="font-heading text-lg text-foreground-900">
                      {comic.chapters[selectedChapter].title}
                    </h2>
                    <button
                      onClick={() => setSelectedChapter(null)}
                      className="text-xs text-primary-500 hover:text-primary-600 mt-1 cursor-pointer"
                    >
                      返回章节列表
                    </button>
                  </div>

                  <button
                    onClick={() => setSelectedChapter(Math.min(comic.chapters.length - 1, selectedChapter + 1))}
                    disabled={selectedChapter === comic.chapters.length - 1}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-label transition-colors cursor-pointer ${
                      selectedChapter === comic.chapters.length - 1
                        ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                        : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500'
                    }`}
                  >
                    下一话
                    <i className="ri-arrow-right-s-line" style={{ width: '16px', height: '16px' }}></i>
                  </button>
                </div>

                {/* Reading Area (scroll-down style) */}
                <div className="space-y-3">
                  {getReadingImages(selectedChapter).map((imgUrl, i) => (
                    <div key={i} className="bg-background-100 rounded-xl overflow-hidden border border-background-200/70">
                      <img
                        src={imgUrl}
                        alt={`${comic.title} - ${comic.chapters[selectedChapter].title} 第${i + 1}页`}
                        title={`${comic.title} 下拉式阅读`}
                        className="w-full h-auto"
                      />
                    </div>
                  ))}
                </div>

                {/* Bottom Navigation */}
                <div className="flex items-center justify-between mt-8 pb-8">
                  <button
                    onClick={() => setSelectedChapter(Math.max(0, selectedChapter - 1))}
                    disabled={selectedChapter === 0}
                    className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-label transition-colors cursor-pointer ${
                      selectedChapter === 0
                        ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                        : 'bg-primary-500 text-white hover:bg-primary-600'
                    }`}
                  >
                    <i className="ri-arrow-left-s-line" style={{ width: '16px', height: '16px' }}></i>
                    上一话
                  </button>

                  <button
                    onClick={() => setSelectedChapter(null)}
                    className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-label hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-list-check" style={{ width: '16px', height: '16px' }}></i>
                    章节列表
                  </button>

                  <button
                    onClick={() => setSelectedChapter(Math.min(comic.chapters.length - 1, selectedChapter + 1))}
                    disabled={selectedChapter === comic.chapters.length - 1}
                    className={`inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-label transition-colors cursor-pointer ${
                      selectedChapter === comic.chapters.length - 1
                        ? 'bg-background-100 text-foreground-300 cursor-not-allowed'
                        : 'bg-primary-500 text-white hover:bg-primary-600'
                    }`}
                  >
                    下一话
                    <i className="ri-arrow-right-s-line" style={{ width: '16px', height: '16px' }}></i>
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: comic.title,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.chapters[0]?.updatedAt,
            dateModified: comic.updatedAt,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://www.hanmancomic.com/comic/${comic.slug}`,
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmancomic.com/' },
              { '@type': 'ListItem', position: 2, name: `${comic.category === 'romance' ? '爱情' : comic.category === 'fantasy' ? '奇幻' : comic.category === 'action' ? '动作' : comic.category === 'mystery' ? '悬疑' : comic.category === 'comedy' ? '搞笑' : comic.category === 'drama' ? '剧情' : '恐怖'}韩漫`, item: `https://www.hanmancomic.com/category/${comic.category}` },
              { '@type': 'ListItem', position: 3, name: comic.title, item: `https://www.hanmancomic.com/comic/${comic.slug}` },
            ],
          }),
        }}
      />
    </>
  );
}